import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { SOURCE_LABELS } from "../shared/sources";

export type ScanProgress = {
  active: boolean;
  brandId: number | null;
  brand: string | null;
  source: string | null;
  sourceIndex: number;
  sourceTotal: number;
  query: string | null;
  queryIndex: number;
  queryTotal: number;
  hits: number;
  startedAt: string | null;
  finishedAt: string | null;
};

type ScanCtx = {
  scanning: boolean;
  progress: ScanProgress | null;
  progressLabel: string;
  progressPct: number;
  beginScan: () => void;
  endScan: () => void;
};

const Ctx = createContext<ScanCtx | null>(null);

export function describeProgress(p: ScanProgress | null): string {
  if (!p || !p.active) return "";
  const src = p.source ? SOURCE_LABELS[p.source] || p.source : "подготовка";
  const srcPos = p.sourceTotal ? ` ${p.sourceIndex}/${p.sourceTotal}` : "";
  const qPos = p.queryTotal > 1 ? ` · запрос ${p.queryIndex}/${p.queryTotal}` : "";
  const hits = p.hits ? ` · +${p.hits}` : "";
  return `${src}${srcPos}${qPos}${hits}`;
}

export function progressFraction(p: ScanProgress | null): number {
  if (!p || !p.active || !p.sourceTotal) return 0;
  const perSource = 1 / p.sourceTotal;
  const within =
    p.queryTotal > 0 ? Math.min(1, p.queryIndex / p.queryTotal) : 0.5;
  const done = Math.max(0, p.sourceIndex - 1) / p.sourceTotal;
  return Math.min(0.99, done + perSource * within);
}

export function ScanProvider({ children }: { children: ReactNode }) {
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState<ScanProgress | null>(null);
  const timer = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (timer.current != null) {
      window.clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const r = await fetch("/api/scan/status");
      const p = (await r.json()) as ScanProgress;
      setProgress(p);
      if (!p.active) {
        stopPolling();
        setScanning(false);
      }
    } catch {
      /* ignore */
    }
  }, [stopPolling]);

  const beginScan = useCallback(() => {
    setScanning(true);
    stopPolling();
    timer.current = window.setInterval(() => void poll(), 1500);
    void poll();
  }, [poll, stopPolling]);

  const endScan = useCallback(() => {
    setScanning(false);
    stopPolling();
  }, [stopPolling]);

  useEffect(() => stopPolling, [stopPolling]);

  const value = useMemo(
    () => ({
      scanning,
      progress,
      progressLabel: describeProgress(progress),
      progressPct: Math.round(progressFraction(progress) * 100),
      beginScan,
      endScan,
    }),
    [scanning, progress, beginScan, endScan],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useScanActivity() {
  const v = useContext(Ctx);
  if (!v) throw new Error("ScanProvider missing");
  return v;
}
